<template>
  <div id="search">
    <!-- Desktop related controls -->
    <template v-if="$vuetify.breakpoint.mdAndUp">
      <sticky-ask-us-btn/>

      <base-container py-0>
        <transition name="slide-y-transition">
          <filters v-show="$app.searchPanel" class="my-2 pb-3" :key="$route.name" />
        </transition>

        <category-seo-text />
      </base-container>

      <base-card color="accent">
        <base-container pa-0>
          <v-layout align-center justify-end row py-2>
            <!-- Additional filters framed, ready to hang and for sale -->
            <v-flex shrink>
              <addional-filters @change="onAdditionalFiltersChanged" />
            </v-flex>

            <v-flex shrink ml-4>
              <v-layout align-center justify-end row>
                <v-flex pr-2>
                  <span class="font-weight-bold">{{ $t('SEARCH.SORT') }}:</span>
                </v-flex>
                <v-flex>
                  <v-select
                    solo
                    flat
                    :items="sortTypes"
                    :loading="searching"
                    item-text="text"
                    item-value="value"
                    v-model="sortType"
                    class="sort-select"
                    @change="onSortTypeChanged"
                  />
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </base-container>
      </base-card>
    </template>
    <!-- /Desktop controls -->

    <!-- Mobile controls -->
    <base-container pa-0 v-else>
      <category-seo-text />

      <mobile-search-controls :sortType="sortType" @sortBy="onMobileSortBy" />
    </base-container>
    <!-- /Mobile controls -->

    <!-- Artworks list -->
    <base-container pb-0>
      <div v-if="artworks.length">
        <masonry :cols="{default: 4, 1200: 3,  960: 2, 600: 1}" :gutter="60">
          <tk-artwork
            v-for="artwork in artworks"
            :key="artwork.id"
            :artwork="artwork"
            :showArtistName="true"
            class="mb-5"
          />
        </masonry>
      </div>

      <!-- NO SEARCH RESULTS -->
      <div v-else class="text-xs-center my-5">
        <h1 v-if="!searching" class="title">{{ $t('COMMON.NO_SEARCH') }}</h1>
      </div>
    </base-container>

    <div class="text-xs-center mb-4" v-if="artworks.length">
      <v-pagination
        @input="onPaginationChanged"
        :disabled="searching"
        :value="pagination.currentPage"
        :length="pagination.lastPage"
        :total-visible="$vuetify.breakpoint.smAndDown ? 5 : 7"
      />
    </div>
  </div>
</template>

<script>
import {
  urlQueryToAlgoliaFilters,
  urlQueryToAlgoliaNumericFilters,
} from "@/utils/UrlUtils";
import { ROUTE_QUERY } from "@/AppKeys";
import { ErrorHandler } from "@/utils/ErrorHandler";
import { SearchFilters } from "@/services/search/SearchFilters";
import { RepositoryFactory } from "@/services/RepositoryFactory";
import { sortTypes } from "@/config";

// Components
import Filters from "./Filters/Filters";
import MobileSearchControls from "./Mobile/SearchControls";
import StickyAskUsBtn from "@/components/core/StickyAskUsBtn";
import CategorySeoText from "./CategorySeoText";

import AddionalFilters from "./AddionalFilters";
import { useGTag } from '../../services/google/google-tag-manager'

const searchRepo = RepositoryFactory.search();
const handler = new ErrorHandler();

export default {
  components: {
    CategorySeoText,
    Filters,
    MobileSearchControls,
    StickyAskUsBtn,
    AddionalFilters,
  },

  inject: [ROUTE_QUERY],

  data: () => ({
    searching: false,
    sortType: "artworks", // algolia indexes name
    artworks: [],
    pagination: {},
    filters: new SearchFilters(),
  }),

  computed: {
    sortTypes() {
      return sortTypes.map((s) => ({
        text: this.$t(s.i18Text),
        value: s.value,
      }));
    },
    routeQuery() {
      return this[ROUTE_QUERY];
    },
  },

  created() {
    this.initPage();
  },

  destroyed() {
    console.log("Search page destroyed");
    this.$app.searchPanel = false;
  },

  watch: {
    "$route.query": {
      immediate: true,
      handler: function () {
        console.log("RouteQueryChanged", this.$route.query);
        this.$nextTick(() => this.fetchData());
      },
    },
  },

  methods: {
    // Init page
    initPage() {
      console.log("INIT PAGE...");
      const query = this.$route.query;
      console.log(query);
      if (query.sortby && query.sortby !== this.sortType) {
        console.log("Sort type is not equal");
        this.sortType = query.sortby;
      }

      if (!this.$vuetify.breakpoint.lgAndDown) {
        this.$app.searchPanel = true;
      } else {
        this.$app.searchPanel = true;
      }
    },

    // Fetch artworks from aloglia or backend
    async fetchData() {
      console.log("Searching....", this.sortType);
      this.searching = true;

      window.scrollTo(0, 0);

      const page = this.$route.query.page || 1;

      console.log(`Fetching data with page ${page}`);

      await handler.handle(async () => {
        const searchOption = this.createSearchOption(page);

        if (this.$route.query.keyword) {
          searchOption.query = this.$route.query.keyword;
        }

        const res =
          this.sortType === "color"
            ? await searchRepo.colorSearch(searchOption)
            : await searchRepo.search(searchOption);
        this.artworks = res.data;
        this.pagination = res.pagination;

        useGTag().viewItemList({
          id: 'search',
          name: 'Search',
          artworks: this.artworks
        })
      });

      this.searching = false;
    },

    // Search option to pass to search repository
    createSearchOption(page = 1) {
      const searchOption = {
        page: page - 1, // Note algolia retutns zero-based page number
        index: this.sortType || "artworks",
        locale: this.$i18n.locale,
        filters: urlQueryToAlgoliaFilters(this.$route.query),
        numericFilters: urlQueryToAlgoliaNumericFilters(this.$route.query),
      };

      return searchOption;
    },

    onSortTypeChanged(e) {
      console.log("onSortTypeChanged", e);
      this.routeQuery.push({ sortby: e });
    },

    onAdditionalFiltersChanged(e) {
      console.log("onAdditionalFiltersChanged", e);
      this.routeQuery.push(e);
    },

    onMobileSortBy(e) {
      console.log("onMobileSortBy", e);
      this.sortType = e;
      this.onSortTypeChanged(e);
    },

    onPaginationChanged(e) {
      this.routeQuery.push({ page: e });
      this.fetchData();
    },
  },
};
</script>

<style lang="scss">
.sort-select {
  max-height: 40px;
  width: 200px;
  .v-input__control {
    min-height: 40px !important;
  }
}
</style>
